import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, CircleF } from '@react-google-maps/api';
import { io } from 'socket.io-client';
import { Bell, MapPin, ShieldCheck, ChevronLeft, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import { liveTrackingService } from '../../../../../services/tracking/liveTrackingService';
import { getTimeAgo, formatBookingId } from '../../../utils/trackingHelpers';
import { mapContainerStyle, premiumMapStyle, markerIcon } from '../../../../../data/tracking/trackingData';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function AgentLiveTrackingMap() {
  const location = useLocation();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const [voucherId, setVoucherId] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [resolvedVoucherId, setResolvedVoucherId] = useState(null);
  const [guests, setGuests] = useState([]);
  const [activeSessions, setActiveSessions] = useState({});
  const [selectedGuestName, setSelectedGuestName] = useState(null);
  const [loading, setLoading] = useState(true);

  const socketRef = useRef(null);
  const mapRef = useRef(null);

  // Parse URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search || window.location.search);
    const vId = params.get('voucherId');
    const gName = params.get('guestName');
    if (vId) {
      setVoucherId(vId);
    }
    if (gName) {
      setSelectedGuestName(decodeURIComponent(gName));
    }
  }, [location.search]);

  const fetchVoucherAndGuests = useCallback(async (isSilent = false) => {
    if (!voucherId) return;
    try {
      if (!isSilent) setLoading(true);

      // Concurrent fetch: load voucher info and guest locations in parallel
      const [voucherRes, guestsRes] = await Promise.all([
        liveTrackingService.getVoucherById(voucherId).catch(() => null),
        liveTrackingService.getGuests(voucherId).catch(() => null)
      ]);

      let matchedVoucher = voucherRes;
      let guestsData = guestsRes;

      // Fallback only if direct query didn't resolve voucher
      if (!matchedVoucher) {
        const listResponse = await liveTrackingService.getVouchers().catch(() => ({ vouchers: [] }));
        const query = voucherId.toLowerCase().trim();
        matchedVoucher = (listResponse.vouchers || []).find((v) => {
          const bId = v.booking_id || v.bookingID || '';
          const shareToken = v.location_sharing?.share_token || '';
          const guestName = v.guest_information?.guest_name || '';
          return (
            bId.toLowerCase() === query ||
            v._id === voucherId ||
            shareToken.toLowerCase() === query ||
            guestName.toLowerCase().includes(query)
          );
        });

        if (matchedVoucher && !guestsData) {
          guestsData = await liveTrackingService.getGuests(matchedVoucher._id).catch(() => []);
        }
      }

      if (matchedVoucher) {
        const resolvedBId = matchedVoucher.booking_id || matchedVoucher.bookingID || voucherId;
        setBookingId(resolvedBId);
        setResolvedVoucherId(matchedVoucher._id);
      } else {
        setBookingId(voucherId);
      }

      if (guestsData && Array.isArray(guestsData)) {
        setGuests(guestsData);

        // Seed activeSessions from database values with strict isolation
        const freshSessions = {};
        guestsData.forEach((g) => {
          const hasCoords = g.lat !== null && g.lng !== null && g.lat !== undefined && g.lng !== undefined && g.lat !== 0 && g.lng !== 0;
          freshSessions[g.name.toLowerCase()] = {
            guestName: g.name,
            uniqueTrackingId: g.unique_tracking_id || g.tracking_id || null,
            lat: hasCoords ? parseFloat(g.lat) : null,
            lng: hasCoords ? parseFloat(g.lng) : null,
            accuracy: parseFloat(g.accuracy) || 0,
            timestamp: g.last_updated || g.last_seen || null,
            is_active: Boolean(g.is_active || g.is_tracking)
          };
        });

        setActiveSessions((prev) => {
          const merged = { ...freshSessions };
          // Preserve recent live socket updates if received within the last 3 minutes
          Object.keys(prev).forEach((key) => {
            if (prev[key]?.is_active && !merged[key]?.is_active) {
              const prevTime = new Date(prev[key].timestamp || 0).getTime();
              if (Date.now() - prevTime < 3 * 60 * 1000) {
                merged[key] = prev[key];
              }
            }
          });
          return merged;
        });
      }
    } catch (err) {
      console.error('Failed to load live tracking data:', err);
      if (!isSilent) toast.error('Failed to load guest list');
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [voucherId]);

  useEffect(() => {
    fetchVoucherAndGuests();
  }, [fetchVoucherAndGuests]);

  // Lightweight background polling (Every 25 seconds) - silent to avoid flicker
  useEffect(() => {
    if (!voucherId) return;
    const interval = setInterval(() => {
      fetchVoucherAndGuests(true);
    }, 25000);
    return () => clearInterval(interval);
  }, [voucherId, fetchVoucherAndGuests]);

  useEffect(() => {
    if (!resolvedVoucherId) return;

    const socket = io(SERVER_URL);
    if (!socket || typeof socket.on !== 'function') return;
    socketRef.current = socket;

    let agentId = localStorage.getItem('agent_id') || null;
    try {
      const token = localStorage.getItem('token_agents');
      if (token) {
        const decoded = jwtDecode(token);
        agentId = decoded?._id || decoded?.id || agentId;
      }
    } catch (err) {
      console.error('Error decoding token for socket subscription:', err);
    }

    socket.on('connect', () => {
      socket.emit('join-agent-voucher', {
        voucherId: resolvedVoucherId,
        agentId: agentId
      });

      if (agentId) {
        socket.emit('join-agent-home', {
          agentId: agentId
        });
      }
    });

    socket.on('session-started', (data) => {
      const guestName = data.guestName || data.guest_name;
      const bookingIdFromEvent = data.booking_id || bookingId;
      const trackingId = data.uniqueTrackingId || '';
      if (guestName) {
        const toastId = `session-start-${trackingId || guestName.toLowerCase()}`;
        const toastMsg = bookingIdFromEvent
          ? `${guestName} (${formatBookingId(bookingIdFromEvent)}) started sharing location!`
          : `${guestName} started sharing location!`;
        toast.success(toastMsg, { id: toastId });
        fetchVoucherAndGuests(true);
      }
    });

    socket.on('receive-location', (data) => {
      const guestName = data.guestName || data.guest_name;
      const lat = parseFloat(data.lat);
      const lng = parseFloat(data.lng);

      if (!guestName || isNaN(lat) || isNaN(lng)) return;
      if (lat === 0 && lng === 0) return;

      setActiveSessions((prev) => ({
        ...prev,
        [guestName.toLowerCase()]: {
          ...data,
          guestName,
          lat,
          lng,
          accuracy: parseFloat(data.accuracy) || 0,
          timestamp: data.timestamp || new Date().toISOString(),
          is_active: true
        }
      }));
    });

    socket.on('sharing-stopped', (data) => {
      const guestName = data.guestName || data.guest_name;
      if (guestName) {
        setActiveSessions((prev) => {
          const key = guestName.toLowerCase();
          const existing = prev[key];
          return {
            ...prev,
            [key]: {
              guestName,
              lat: existing?.lat ?? (data.last_known_location?.lat ? parseFloat(data.last_known_location.lat) : null),
              lng: existing?.lng ?? (data.last_known_location?.lng ? parseFloat(data.last_known_location.lng) : null),
              accuracy: existing?.accuracy ?? (parseFloat(data.last_known_location?.accuracy) || 0),
              timestamp: existing?.timestamp ?? (data.last_known_location?.updated_at || null),
              is_active: false
            }
          };
        });
      } else {
        setActiveSessions((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach(k => {
            updated[k] = { ...updated[k], is_active: false };
          });
          return updated;
        });
      }
      fetchVoucherAndGuests(true);
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [resolvedVoucherId, bookingId, fetchVoucherAndGuests]);

  const handlePing = async (guestName) => {
    try {
      await liveTrackingService.pingGuest({
        bookingId: bookingId || voucherId,
        guestName,
        message: 'Agent has requested for location. Please start sharing your live location.'
      });
      toast.success(`Ping request sent to ${guestName}`);
    } catch (err) {
      console.error('Failed to ping:', err);
      toast.error(err.response?.data?.message || 'Failed to send ping notification');
    }
  };

  const handlePingAll = async () => {
    try {
      await liveTrackingService.pingGuest({
        bookingId: bookingId || voucherId,
        guestName: 'all',
        message: 'Agent has requested for location. Please start sharing your live location.'
      });
      toast.success('Ping request sent to all guests');
    } catch (err) {
      console.error('Failed to ping all:', err);
      toast.error(err.response?.data?.message || 'No traveller logged in - ask traveller to download app');
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Pan to selected guest when they have valid coordinates
  useEffect(() => {
    if (!mapRef.current || !selectedGuestName) return;
    const target = activeSessions[selectedGuestName.toLowerCase()];
    if (target && target.lat && target.lng && target.lat !== 0 && target.lng !== 0) {
      mapRef.current.panTo({ lat: target.lat, lng: target.lng });
      mapRef.current.setZoom(17);
    }
  }, [activeSessions, selectedGuestName]);

  const activeGuestSession = selectedGuestName ? activeSessions[selectedGuestName.toLowerCase()] : null;
  const isSelectedGuestLive = Boolean(
    selectedGuestName &&
    activeGuestSession &&
    activeGuestSession.is_active === true
  );
  const isSelectedGuestOffline = Boolean(selectedGuestName && !isSelectedGuestLive);

  const hasValidCoordinates = Boolean(
    selectedGuestName &&
    activeGuestSession &&
    activeGuestSession.lat !== null &&
    activeGuestSession.lng !== null &&
    activeGuestSession.lat !== undefined &&
    activeGuestSession.lng !== undefined &&
    activeGuestSession.lat !== 0 &&
    activeGuestSession.lng !== 0
  );

  const isSelectedGuestWaitingGPS = Boolean(
    isSelectedGuestLive && !hasValidCoordinates
  );

  // Active count strictly counts sessions that are genuinely active
  const activeCount = Object.values(activeSessions).filter((s) => s.is_active === true).length;

  const sortedGuests = [...guests].sort((a, b) => {
    const aSession = activeSessions[a.name.toLowerCase()];
    const bSession = activeSessions[b.name.toLowerCase()];
    const aLive = Boolean(aSession ? aSession.is_active === true : (a.is_active || a.is_tracking));
    const bLive = Boolean(bSession ? bSession.is_active === true : (b.is_active || b.is_tracking));
    if (aLive && !bLive) return -1;
    if (!aLive && bLive) return 1;
    return 0;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] gap-4 animate-fade-in-up font-sans text-slate-800">
      {/* Top Header Card */}
      <div className="flex items-center justify-between bg-white px-5 py-3.5 rounded-2xl border border-slate-200/60 shadow-xs">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-800 cursor-pointer"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-slate-900">Live Tracking</h1>
              <span className="bg-slate-100 text-slate-600 font-mono font-bold px-2 py-0.5 rounded text-xs uppercase border border-slate-200/50">
                {formatBookingId(bookingId || voucherId)}
              </span>
            </div>
            <p className="text-slate-500 text-xs mt-0.5">
              {guests.length} {guests.length === 1 ? 'guest' : 'guests'} linked • {activeCount} active
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              await fetchVoucherAndGuests(false);
              toast.success('Location tracking data refreshed');
            }}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition-colors text-xs font-bold uppercase tracking-wider shadow-xs disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>

          <button
            onClick={handlePingAll}
            className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition-colors text-xs font-bold uppercase tracking-wider shadow-xs cursor-pointer"
          >
            <Bell size={13} />
            Ping All Guests
          </button>
        </div>
      </div>

      {/* Split Panels */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
        {/* Left Side: Guest List */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200/60 shadow-xs flex flex-col min-h-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider">Guests & Travelers</h3>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-slate-400 font-semibold">Loading guests...</span>
              </div>
            ) : sortedGuests.length > 0 ? (
              sortedGuests.map((guest, idx) => {
                const session = activeSessions[guest.name.toLowerCase()];
                const sessionTime = session?.timestamp ? new Date(session.timestamp).getTime() : 0;
                const isRecent = sessionTime > 0 && (Date.now() - sessionTime < 3 * 60 * 1000);
                const isLive = Boolean(session ? (session.is_active === true && isRecent) : (guest.is_active || guest.is_tracking));

                const hasCoords = Boolean(
                  session
                    ? (session.lat && session.lng && session.lat !== 0 && session.lng !== 0)
                    : (guest.lat && guest.lng && guest.lat !== 0 && guest.lng !== 0)
                );
                const isUnavailable = guest.status === 'unavailable' || guest.location_unavailable;
                const timestamp = session?.timestamp || guest.last_updated || guest.last_seen;
                const isSelected = selectedGuestName?.toLowerCase() === guest.name.toLowerCase();

                return (
                  <div
                    key={idx}
                    className={`p-4 transition-colors flex items-center justify-between ${
                      isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : 'bg-white hover:bg-slate-50/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-xs border border-slate-200/50">
                        {guest.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 text-sm leading-snug">{guest.name}</div>
                        {isLive ? (
                          <div className="flex flex-col mt-0.5">
                            <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                              <span>Sharing location</span>
                            </div>
                            <span className="text-[11px] text-slate-400 pl-3">
                              Updated {getTimeAgo(timestamp)}
                            </span>
                          </div>
                        ) : isUnavailable ? (
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            <span>Location unavailable</span>
                          </div>
                        ) : hasCoords ? (
                          <div className="flex flex-col mt-0.5">
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              <span>Not sharing</span>
                            </div>
                            <span className="text-[11px] text-slate-400 pl-3">
                              Last seen {getTimeAgo(timestamp)}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col mt-0.5">
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              <span>Not sharing</span>
                            </div>
                            <span className="text-[11px] text-slate-400 pl-3">
                              No location history
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {hasCoords && (
                        <button
                          onClick={() => {
                            setSelectedGuestName(guest.name);
                          }}
                          className={`text-xs font-semibold transition-all px-2.5 py-1.5 rounded-lg border cursor-pointer ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-xs'
                              : isLive
                              ? 'text-primary bg-primary/5 border-primary/20 hover:bg-primary/10'
                              : 'text-slate-600 bg-slate-50 border-slate-200 hover:bg-slate-100'
                          }`}
                          title={isLive ? 'Track Live Location' : 'View Location on Map'}
                        >
                          {isLive ? 'Track' : 'View'}
                        </button>
                      )}

                      {!isLive && (
                        <button
                          onClick={() => handlePing(guest.name)}
                          className="text-xs font-semibold text-slate-500 hover:text-primary p-1.5 border border-slate-200 hover:border-primary/20 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                          title="Request traveler to start live sharing"
                        >
                          <Bell size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <p className="text-xs text-slate-400 italic">No registered travelers linked.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Map Card */}
        <div className="lg:col-span-9 bg-white rounded-2xl border border-slate-200/60 shadow-xs overflow-hidden flex flex-col min-h-0 relative">
          <div className="flex-1 relative min-h-[350px]">
            {!isLoaded ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50 flex-col gap-2 z-20">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-medium text-slate-400">Loading Map...</span>
              </div>
            ) : !selectedGuestName ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/50 flex-col gap-3 text-center p-8 z-10">
                <div className="w-12 h-12 bg-white rounded-full border border-slate-200/60 shadow-xs flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-400" />
                </div>
                <div className="max-w-xs">
                  <h4 className="text-sm font-bold text-slate-800 mb-1">Select a Traveler</h4>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    Click <strong>Track</strong> or <strong>View</strong> next to a traveler's name to view their location on the map.
                  </p>
                </div>
              </div>
            ) : isSelectedGuestWaitingGPS ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/90 flex-col gap-3 text-center p-8 z-10 animate-fade-in">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <div className="max-w-xs">
                  <h4 className="text-sm font-bold text-slate-800 mb-1">Connecting to GPS...</h4>
                  <p className="text-slate-500 text-xs leading-relaxed animate-pulse">
                    Waiting for live coordinates from <strong>{selectedGuestName}</strong>. Make sure their device has location services enabled.
                  </p>
                </div>
              </div>
            ) : hasValidCoordinates ? (
              <div className="w-full h-full relative">
                {/* Minimalist, Elegant Status Badge for Offline State (TRRIP Aesthetic) */}
                {isSelectedGuestOffline && (
                  <div className="absolute top-4 left-4 z-10 flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2 rounded-xl border border-slate-200 shadow-sm animate-fade-in text-xs text-slate-700">
                    <span className="w-2 h-2 rounded-full bg-slate-400 inline-block flex-shrink-0" />
                    <span>
                      <strong className="text-slate-900">{selectedGuestName}</strong> • Not sharing • Last seen {getTimeAgo(activeGuestSession.timestamp)}
                    </span>
                    <button
                      onClick={() => handlePing(selectedGuestName)}
                      className="ml-2 font-semibold text-primary hover:text-primary/80 flex items-center gap-1 cursor-pointer flex-shrink-0"
                    >
                      <Bell size={11} /> Ping
                    </button>
                  </div>
                )}

                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{ lat: activeGuestSession.lat, lng: activeGuestSession.lng }}
                  zoom={16}
                  onLoad={onMapLoad}
                  options={{
                    styles: premiumMapStyle,
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    scaleControl: true,
                    mapTypeControl: false,
                    fullscreenControl: true
                  }}
                >
                  <MarkerF
                    position={{ lat: activeGuestSession.lat, lng: activeGuestSession.lng }}
                    icon={{
                      ...markerIcon,
                      fillColor: isSelectedGuestLive ? '#067978' : '#64748b',
                      strokeColor: '#ffffff'
                    }}
                  >
                    <InfoWindowF position={{ lat: activeGuestSession.lat, lng: activeGuestSession.lng }}>
                      <div className="p-1 min-w-[170px] font-sans">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="font-bold text-slate-900 text-xs">{selectedGuestName}</span>
                          <span
                            className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider border ${isSelectedGuestLive
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/60'
                                : 'bg-slate-100 text-slate-600 border-slate-200/60'
                              }`}
                          >
                            {isSelectedGuestLive ? 'Sharing' : 'Not sharing'}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 mb-1">
                          {isSelectedGuestLive ? 'Sharing location • ' : 'Not sharing • '}
                          {isSelectedGuestLive
                            ? `Updated ${getTimeAgo(activeGuestSession.timestamp)}`
                            : `Last seen ${getTimeAgo(activeGuestSession.timestamp)}`}
                        </div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-1 border-t border-slate-100 pt-1 mt-1">
                          <ShieldCheck size={11} className={isSelectedGuestLive ? 'text-primary' : 'text-slate-400'} />
                          <span>
                            Accuracy: <strong className="text-slate-700 font-semibold">{Math.round(activeGuestSession.accuracy)}m</strong>
                          </span>
                        </div>
                      </div>
                    </InfoWindowF>
                  </MarkerF>
                  <CircleF
                    center={{ lat: activeGuestSession.lat, lng: activeGuestSession.lng }}
                    radius={activeGuestSession.accuracy || 20}
                    options={{
                      fillColor: isSelectedGuestLive ? '#067978' : '#94a3b8',
                      fillOpacity: isSelectedGuestLive ? 0.08 : 0.03,
                      strokeColor: isSelectedGuestLive ? '#067978' : '#94a3b8',
                      strokeOpacity: isSelectedGuestLive ? 0.25 : 0.15,
                      strokeWeight: 1
                    }}
                  />
                </GoogleMap>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 flex-col gap-3 text-center p-8 z-10 animate-fade-in">
                <div className="w-12 h-12 bg-white rounded-full border border-slate-200/80 shadow-xs flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-400" />
                </div>
                <div className="max-w-xs">
                  <h4 className="text-sm font-bold text-slate-800 mb-1">{selectedGuestName} is Offline</h4>
                  <p className="text-slate-500 text-xs leading-relaxed mb-3">
                    No location history recorded yet for this traveler. Request them to start live sharing.
                  </p>
                  <button
                    onClick={() => handlePing(selectedGuestName)}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl transition-all shadow-xs inline-flex items-center gap-1.5 cursor-pointer"
                  >
                    <Bell size={12} /> Ping {selectedGuestName}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
